import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import { styled } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import {
  ButtonBase,
  type ButtonBaseProps,
  Dialog,
  DialogContent,
  type DialogProps,
} from '@mui/material';

const CustomCommandPrimitive = styled(CommandPrimitive)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
  },
  '& [cmdk-group-heading]': {
    padding: theme.spacing(1, 0),
    weight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
  },
  '& [cmdk-group]:not([hidden]) ~[cmdk-group]': {
    paddingTop: 0,
  },
  '& [cmdk-group]': {
    padding: theme.spacing(1, 0),
  },
  '& [cmdk-input-wrapper] svg': {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    height: 20,
    width: 20,
    flexShrink: 0,
    opacity: 0.5,
  },
  '& [cmdk-input]': {
    height: '40px',
  },
  '& [cmdk-item]': {
    padding: theme.spacing(1, 1.5),
  },
  '& [cmdk-item] svg': {
    height: 20,
    width: 20,
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const CustomCommandDialogTrigger = styled(ButtonBase)(({ theme }) => ({
  cursor: 'text',
  display: 'inline-flex',
  boxSizing: 'border-box',
  justifyContent: 'start',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  fontFamily: '"Poppins", sans-serif',
  fontWeight: theme.typography.fontWeightLight,
  backgroundColor: 'rgb(237, 239, 243)',
  minWidth: '180px',
  padding: theme.spacing(1, 1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& svg': {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    height: 20,
    width: 20,
    flexShrink: 0,
    opacity: 0.5,
  },
}));

const CommandTrigger = ({ children, ...props }: ButtonBaseProps) => {
  return (
    <CustomCommandDialogTrigger disableRipple {...props}>
      {children}
    </CustomCommandDialogTrigger>
  );
};

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CustomCommandPrimitive ref={ref} className={className} {...props} />
));
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="p-0 overflow-hidden">
        <Command>{children}</Command>
      </DialogContent>
    </Dialog>
  );
};

const CustomCommandInput = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(0, 1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CustomSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
  height: 20,
  width: 20,
  flexShrink: 0,
  opacity: 0.5,
}));

const CustomCommandPrimitiveInput = styled(CommandPrimitive.Input)(
  ({ theme }) => ({
    flex: 1,
    height: '40px',
    width: '100%',
    borderRadius: '16px',
    padding: theme.spacing(0, 1),
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    fontFamily: '"Poppins", sans-serif',
    fontSize: '14px',
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.5,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  }),
);

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <CustomCommandInput cmdk-input-wrapper="">
    <CustomSearchIcon />
    <CustomCommandPrimitiveInput
      ref={ref}
      className={className}
      {...props}
      autoFocus
    />
  </CustomCommandInput>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CustomCommandPrimitiveList = styled(CommandPrimitive.List)(() => ({
  maxHeight: '300px',
  overflowY: 'auto',
  overflowX: 'hidden',
}));

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CustomCommandPrimitiveList ref={ref} className={className} {...props} />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CustomCommandPrimitiveEmpty = styled(CommandPrimitive.Empty)(
  ({ theme }) => ({
    padding: theme.spacing(6, 0),
    textAlign: 'center',
    fontSize: '14px',
    color: theme.palette.text.secondary,
    fontFamily: '"Poppins", sans-serif',
  }),
);

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => <CustomCommandPrimitiveEmpty ref={ref} {...props} />);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CustomCommandPrimitiveGroup = styled(CommandPrimitive.Group)(
  ({ theme }) => ({
    padding: theme.spacing(1, 0),
    '& [cmdk-group-heading]': {
      padding: theme.spacing(1, 0.75),
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.secondary,
    },
    '& [cmdk-group]:not([hidden]) ~[cmdk-group]': {
      paddingTop: 0,
    },
    '& [cmdk-group]': {
      padding: theme.spacing(1, 0),
    },
  }),
);

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CustomCommandPrimitiveGroup ref={ref} className={className} {...props} />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CustomCommandPrimitiveSeparator = styled(CommandPrimitive.Separator)(
  ({ theme }) => ({
    margin: theme.spacing(1, 0),
    height: '1px',
    backgroundColor: theme.palette.divider,
  }),
);

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CustomCommandPrimitiveSeparator ref={ref} className={className} {...props} />
));

CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CustomCommandPrimitiveItem = styled(CommandPrimitive.Item)(
  ({ theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    cursor: 'default',
    userSelect: 'none',
    padding: theme.spacing(1, 0.75),
    borderRadius: theme.shape.borderRadius,
    fontSize: '14px',
    color: theme.palette.text.primary,
    outline: 'none',
    '&[aria-selected=“true”]': {
      backgroundColor: theme.palette.action.selected,
    },
    '&[data-highlighted]': {
      backgroundColor: theme.palette.action.hover,
    },
    '&[data-selected]': {
      backgroundColor: theme.palette.action.selected,
    },
    '&[data-disabled]': {
      pointerEvents: 'none',
      opacity: 0.5,
    },
  }),
);

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CustomCommandPrimitiveItem ref={ref} className={className} {...props} />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CustomCommandShortcut = styled('span')(({ theme }) => ({
  fontSize: '12px',
  fontWeight: theme.typography.fontWeightLight,
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.25, 0.75),
  marginLeft: 'auto',
}));

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return <CustomCommandShortcut className={className} {...props} />;
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  CommandTrigger,
};
